import { useEffect, useMemo, useState } from "react";
import { http, fmtINR, fmtNum, fmtPct } from "@/lib/api";
import {
    ReportTable,
    ReportTHead,
    SortableTH,
    FilterBar,
    FilterSelect,
    SearchInput,
} from "@/components/ReportTable";
import {
    BarChart,
    Bar,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
} from "recharts";

const PIE_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function TertiaryReport() {
    const [facets, setFacets] = useState({});
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState({ by: "invoice_date", dir: -1 });
    const [filters, setFilters] = useState({
        state: "All",
        distributor: "All",
        month: "All",
        category: "All",
        gender: "All",
        bs_reg: "All",
    });
    const [q, setQ] = useState("");

    useEffect(() => {
        http.get("/reports/tertiary/facets").then((r) => setFacets(r.data));
        http.get("/reports/tertiary/summary").then((r) => setSummary(r.data));
    }, []);

    useEffect(() => {
        const params = { limit: 300, sort_by: sort.by, sort_dir: sort.dir, q };
        Object.entries(filters).forEach(([k, v]) => {
            if (v !== "All") params[k] = v;
        });
        http.get("/reports/tertiary", { params }).then((r) => {
            setRows(r.data.rows);
            setTotal(r.data.total);
        });
    }, [filters, sort, q]);

    const t = summary?.totals;

    const emptyState = t && t.rows === 0;

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Reports · Tertiary</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Monthly Tertiary Sales</h1>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Retailer sell-out invoices ingested from monthly / daily billing feeds.
                    </p>
                </div>
                <div className="text-[11px] text-muted-foreground">
                    {t ? `${fmtNum(t.rows)} invoice lines · ${fmtNum(rows.length)}/${fmtNum(total)} shown` : "…"}
                </div>
            </div>

            {emptyState && <EmptyState />}

            {t && !emptyState && (
                <>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                        <Stat label="Rows" value={fmtNum(t.rows)} />
                        <Stat label="Quantity" value={fmtNum(t.qty)} />
                        <Stat label="MRP Value" value={fmtINR(t.mrp)} />
                        <Stat label="Discount" value={fmtINR(t.discount)} tone="amber" />
                        <Stat label="Net Value" value={fmtINR(t.net)} tone="green" />
                    </div>

                    <div className="grid grid-cols-12 gap-3">
                        <ChartCard title="Net Value by State" col={5}>
                            <ResponsiveContainer>
                                <BarChart data={summary.by_state} margin={{ left: 10, right: 10, top: 4, bottom: 4 }}>
                                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                                    <XAxis dataKey="key" stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} />
                                    <YAxis
                                        stroke="hsl(var(--muted-foreground))"
                                        fontSize={10}
                                        width={40}
                                        tickLine={false}
                                        tickFormatter={(v) => (v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v)}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: "hsl(var(--card))",
                                            border: "1px solid hsl(var(--border))",
                                            fontSize: 12,
                                        }}
                                        formatter={(v) => fmtINR(v)}
                                    />
                                    <Bar dataKey="net" fill="hsl(var(--chart-1))" radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                        <ChartCard title="Category Mix" col={4}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Tooltip
                                        contentStyle={{
                                            background: "hsl(var(--card))",
                                            border: "1px solid hsl(var(--border))",
                                            fontSize: 12,
                                        }}
                                        formatter={(v) => fmtINR(v)}
                                    />
                                    <Pie
                                        data={summary.by_category}
                                        dataKey="net"
                                        nameKey="key"
                                        innerRadius={40}
                                        outerRadius={70}
                                        paddingAngle={2}
                                    >
                                        {summary.by_category.map((_, i) => (
                                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="mt-1 space-y-0.5">
                                {summary.by_category.map((b, i) => (
                                    <div key={b.key} className="flex items-center justify-between text-[10px]">
                                        <div className="flex items-center gap-1.5">
                                            <span
                                                className="h-2 w-2 rounded-full"
                                                style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                                            />
                                            {b.key || "—"}
                                        </div>
                                        <span className="mono-num text-muted-foreground">{fmtINR(b.net)}</span>
                                    </div>
                                ))}
                            </div>
                        </ChartCard>
                        <ChartCard title="Top Retailers (Net)" col={3}>
                            <div className="space-y-1.5">
                                {summary.top_retailers.slice(0, 8).map((r, i) => (
                                    <div key={i} className="rounded-sm bg-muted/40 p-1.5">
                                        <div className="flex items-center justify-between text-[11px]">
                                            <span className="truncate pr-1 font-medium">{r.retailer}</span>
                                            <span className="mono-num text-[11px]">{fmtINR(r.net)}</span>
                                        </div>
                                        <div className="text-[10px] text-muted-foreground">
                                            {r.distributor} · {fmtNum(r.qty)} qty
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ChartCard>
                    </div>
                </>
            )}

            <FilterBar>
                <SearchInput
                    value={q}
                    onChange={setQ}
                    placeholder="Search retailer / invoice / EAN…"
                    testid="tertiary-search"
                />
                <FilterSelect
                    label="State"
                    value={filters.state}
                    options={facets.states || []}
                    onChange={(v) => setFilters({ ...filters, state: v })}
                    testid="tertiary-filter-state"
                />
                <FilterSelect
                    label="Distributor"
                    value={filters.distributor}
                    options={facets.distributors || []}
                    onChange={(v) => setFilters({ ...filters, distributor: v })}
                    testid="tertiary-filter-distributor"
                />
                <FilterSelect
                    label="Month"
                    value={filters.month}
                    options={facets.months || []}
                    onChange={(v) => setFilters({ ...filters, month: v })}
                    testid="tertiary-filter-month"
                />
                <FilterSelect
                    label="Category"
                    value={filters.category}
                    options={facets.categories || []}
                    onChange={(v) => setFilters({ ...filters, category: v })}
                    testid="tertiary-filter-category"
                />
                <FilterSelect
                    label="Gender"
                    value={filters.gender}
                    options={facets.genders || []}
                    onChange={(v) => setFilters({ ...filters, gender: v })}
                    testid="tertiary-filter-gender"
                />
                <FilterSelect
                    label="Channel"
                    value={filters.bs_reg}
                    options={facets.bs_regs || []}
                    onChange={(v) => setFilters({ ...filters, bs_reg: v })}
                    testid="tertiary-filter-bs"
                />
            </FilterBar>

            <ReportTable testid="tertiary-table">
                <ReportTHead>
                    <SortableTH label="Date" field="invoice_date" sort={sort} setSort={setSort} />
                    <SortableTH label="State" field="state" sort={sort} setSort={setSort} />
                    <SortableTH label="Distributor" field="distributor" sort={sort} setSort={setSort} />
                    <SortableTH label="Retailer" field="retailer" sort={sort} setSort={setSort} />
                    <SortableTH label="Invoice" field="invoice_no" sort={sort} setSort={setSort} />
                    <SortableTH label="EAN" field="ean" sort={sort} setSort={setSort} />
                    <SortableTH label="Cat" field="category" sort={sort} setSort={setSort} />
                    <SortableTH label="Qty" field="qty" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="MRP" field="mrp_val" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="Disc" field="discount" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="Net" field="net_value" sort={sort} setSort={setSort} align="right" />
                    <SortableTH label="Disc %" sort={sort} setSort={setSort} align="right" />
                </ReportTHead>
                <tbody>
                    {rows.map((r) => {
                        const dpct = r.mrp_val ? (r.discount / r.mrp_val) * 100 : 0;
                        return (
                            <tr key={r.id} data-testid={`tertiary-row-${r.id}`} className="border-t border-border hover:bg-muted/40">
                                <td className="mono-num px-3 py-1.5 text-muted-foreground">{r.invoice_date}</td>
                                <td className="px-3 py-1.5">{r.state}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">{r.distributor}</td>
                                <td className="px-3 py-1.5">{r.retailer}</td>
                                <td className="mono-num px-3 py-1.5">{r.invoice_no}</td>
                                <td className="mono-num px-3 py-1.5 text-muted-foreground">{r.ean}</td>
                                <td className="px-3 py-1.5">{r.category}</td>
                                <td className="mono-num px-3 py-1.5 text-right">{fmtNum(r.qty)}</td>
                                <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.mrp_val)}</td>
                                <td className="mono-num px-3 py-1.5 text-right text-amber-600">{fmtINR(r.discount)}</td>
                                <td className="mono-num px-3 py-1.5 text-right font-semibold">{fmtINR(r.net_value)}</td>
                                <td className="mono-num px-3 py-1.5 text-right text-muted-foreground">{fmtPct(dpct)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </ReportTable>
        </div>
    );
}

function Stat({ label, value, tone }) {
    const c =
        tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : tone === "red" ? "text-red-500" : "";
    return (
        <div className="rounded-sm border border-border bg-card p-3">
            <div className="mlabel">{label}</div>
            <div className={`mono-num mt-1 text-xl font-semibold ${c}`}>{value}</div>
        </div>
    );
}

function ChartCard({ title, col, children }) {
    return (
        <div className={`col-span-12 rounded-sm border border-border bg-card p-4 lg:col-span-${col}`}>
            <div className="mlabel mb-2">{title}</div>
            <div className="h-[200px]">{children}</div>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="rounded-sm border border-dashed border-border bg-card p-8 text-center">
            <div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-sm bg-primary/10 text-primary">
                <span className="mono-num text-lg">0</span>
            </div>
            <div className="text-sm font-semibold">No tertiary sales ingested yet</div>
            <div className="mt-1 text-[12px] text-muted-foreground">
                Upload a Monthly Tertiary sheet from the Reports → Upload page to see live analytics.
            </div>
        </div>
    );
}
